/**
 * Employees: everybody utuwrk has onboarded or offboarded, where each is in
 * their time here, and the way to their work. An index derived from that
 * work, not a record about anybody: nothing on these screens is typed in
 * about a person except their name and a temp's end date (decisions.md,
 * "Employees are an index of the work, with no notes about a person").
 */

import { useMemo, useState, type FormEvent } from "react";
import {
  ACCESS_CHANGE,
  EMPLOYEE_STATES,
  type EmployeeResponse,
  type EmployeeState,
  type EmployeeSummary,
} from "@utuwrk/core";
import { sentence } from "../lib/api.js";
import { useOrg } from "../lib/org.js";
import { auditOf } from "../lib/router.js";
import { EMPLOYEE_STATE_CLASS, EMPLOYEE_STATE_LABEL, dayText, plural, words } from "../lib/words.js";
import { EmptyState, Link, Loading, PageHeader, Problem, useLoad } from "./bits.js";
import { Icon } from "./Icon.js";
import { InstanceRow, canStartHere } from "./Onboardings.js";
import { QueueRow } from "./Queue.js";

function EmployeeStatePill({ state }: { state: EmployeeState }) {
  return <span className={`pill ${EMPLOYEE_STATE_CLASS[state]}`}>{EMPLOYEE_STATE_LABEL[state]}</span>;
}

/** "Starts" before they have, "Started" after, and neither for somebody who never came. */
function startText(employee: EmployeeSummary): string | null {
  if (!employee.startDate) return null;
  const said =
    employee.state === "onboarding" ? "Starts" : employee.state === "withdrawn" ? "Was to start" : "Started";
  return `${said} ${dayText(employee.startDate, true)}`;
}

export function Employees() {
  const { api } = useOrg();
  const { data, error } = useLoad(() => api.employees(), [api]);
  const [search, setSearch] = useState("");
  const [state, setState] = useState<EmployeeState | "">("");

  const counts = useMemo(() => {
    const out = new Map<EmployeeState, number>();
    for (const employee of data?.employees ?? []) out.set(employee.state, (out.get(employee.state) ?? 0) + 1);
    return out;
  }, [data]);

  if (!data && !error) return <Loading title="Employees" />;
  const needle = search.trim().toLowerCase();
  const shown = (data?.employees ?? []).filter(
    (employee) =>
      (!state || employee.state === state) &&
      (!needle ||
        [employee.name, employee.department, employee.siteName, employee.managerName ?? ""].some((value) =>
          value.toLowerCase().includes(needle),
        )),
  );

  return (
    <section className="page">
      <PageHeader
        title="Employees"
        meta="Everybody utuwrk has onboarded or offboarded, and where each is now."
      />
      <Problem>{error}</Problem>
      {data && data.employees.length > 0 ? (
        <div className="panel">
          <div className="controls">
            <label className="field">
              <span>Find</span>
              <input
                type="search"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Name, department, site or manager"
                autoComplete="off"
              />
            </label>
            <label className="field">
              <span>Where they are</span>
              <select value={state} onChange={(event) => setState(event.target.value as EmployeeState | "")}>
                <option value="">Everybody ({data.employees.length})</option>
                {EMPLOYEE_STATES.filter((option) => counts.has(option)).map((option) => (
                  <option key={option} value={option}>
                    {EMPLOYEE_STATE_LABEL[option]} ({counts.get(option)})
                  </option>
                ))}
              </select>
            </label>
          </div>
        </div>
      ) : null}

      {data ? (
        data.employees.length === 0 ? (
          <EmptyState icon="contact" title="Nobody yet">
            Everybody onboarded appears here from the moment it starts.
          </EmptyState>
        ) : shown.length === 0 ? (
          <EmptyState icon="contact" title="Nobody matches that" />
        ) : (
          <ul className="cards">
            {shown.map((employee) => (
              <li key={employee.id}>
                <Link to={{ name: "employee", id: employee.id }} className="card card-link">
                  <div className="card-top">
                    <h2 className="card-title">{employee.name}</h2>
                    <EmployeeStatePill state={employee.state} />
                  </div>
                  <p className="card-meta">
                    {employee.siteName ? <span className="chip">{employee.siteName}</span> : null}
                    {employee.department ? <span>{employee.department}</span> : null}
                    {employee.managerName ? <span>Manager {employee.managerName}</span> : null}
                    {startText(employee) ? <span>{startText(employee)}</span> : null}
                    {employee.endDate ? <span>Contract ends {dayText(employee.endDate, true)}</span> : null}
                    {employee.lastDay ? <span>Last day {dayText(employee.lastDay, true)}</span> : null}
                    {employee.openCards > 0 ? <span>{plural(employee.openCards, "open card")}</span> : null}
                  </p>
                </Link>
              </li>
            ))}
          </ul>
        )
      ) : null}
    </section>
  );
}

export function Employee({ id }: { id: string }) {
  const { api, org } = useOrg();
  const { data, error, setData } = useLoad(() => api.employee(id), [api, id]);

  if (!data && !error) return <Loading title="Employee" />;
  if (!data) {
    return (
      <section className="page">
        <PageHeader back={{ to: { name: "employees" }, label: "Employees" }} title="Employee" />
        <Problem>{error}</Problem>
      </section>
    );
  }

  const { employee, work, openCards } = data;
  const facts: [string, string | null][] = [
    ["Site", employee.siteName || null],
    ["Department", employee.department || null],
    ["Manager", employee.managerName ?? "Nobody yet"],
    ["Status", employee.employmentStatus ? words(employee.employmentStatus) : null],
    [
      employee.state === "withdrawn" ? "Was to start" : "Start date",
      employee.startDate && dayText(employee.startDate, true),
    ],
    ["Contract ends", employee.endDate && dayText(employee.endDate, true)],
    ["Last day", employee.lastDay && dayText(employee.lastDay, true)],
  ];

  return (
    <section className="page">
      <PageHeader
        back={{ to: { name: "employees" }, label: "Employees" }}
        title={employee.name}
        meta={<EmployeeStatePill state={employee.state} />}
        actions={
          canStartHere(org.role) && (employee.state === "active" || employee.state === "onboarding") ? (
            <>
              <Link
                to={{ name: "leave", employeeId: employee.id, trigger: ACCESS_CHANGE }}
                className="button button-quiet"
              >
                Change access
              </Link>
              <Link to={{ name: "leave", employeeId: employee.id }} className="button button-quiet">
                <Icon name="user-minus" size={18} />
                Start an offboarding
              </Link>
            </>
          ) : null
        }
      />
      <div className="panel">
        <dl className="values facts">
          {facts
            .filter((fact): fact is [string, string] => fact[1] !== null)
            .map(([label, value]) => (
              <div key={label}>
                <dt>{label}</dt>
                <dd>{value}</dd>
              </div>
            ))}
        </dl>
      </div>

      {data.canChange &&
      employee.employmentStatus === "temp" &&
      (employee.state === "active" || employee.state === "onboarding") ? (
        <Checkpoint employee={employee} onChange={setData} />
      ) : null}

      {data.canErase && data.privateValues > 0 ? (
        <Erase employee={employee} count={data.privateValues} onChange={setData} />
      ) : null}

      <section className="zone" aria-labelledby="zone-open">
        <h2 id="zone-open" className="zone-title">
          Open now <span className="zone-count">{openCards.length}</span>
        </h2>
        {openCards.length === 0 ? (
          <p className="empty-line">Nothing is open on their work.</p>
        ) : (
          <ul className="cards">
            {openCards.map((entry) => (
              <QueueRow key={`${entry.instanceId}:${entry.card.key}`} entry={entry} about={false} />
            ))}
          </ul>
        )}
      </section>

      <section className="zone" aria-labelledby="zone-work">
        <h2 id="zone-work" className="zone-title">
          Their onboardings, offboardings and access changes <span className="zone-count">{work.length}</span>
        </h2>
        <ul className="cards">
          {work.map((instance) => (
            <InstanceRow key={instance.id} instance={instance} byTemplate />
          ))}
        </ul>
        {data.canReadAudit ? (
          <div className="action-row">
            {work.map((instance) => (
              <Link
                key={instance.id}
                to={auditOf(instance.id, instance.kind)}
                className="button button-quiet"
              >
                Audit log: {instance.templateName}
              </Link>
            ))}
          </div>
        ) : null}
      </section>
    </section>
  );
}

/**
 * Right to erasure (PRD 11.2), for admins and compliance: every private value
 * recorded about this person replaced with a marker naming the request. Asked
 * twice -- the number, then a confirmation -- because nothing brings them back.
 */
function Erase({
  employee,
  count,
  onChange,
}: {
  employee: EmployeeSummary;
  count: number;
  onChange: (response: EmployeeResponse) => void;
}) {
  const { api } = useOrg();
  const [reference, setReference] = useState("");
  const [sure, setSure] = useState(false);
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);

  async function erase(event: FormEvent) {
    event.preventDefault();
    if (!sure) {
      setSure(true);
      return;
    }
    setBusy(true);
    setProblem(null);
    try {
      onChange(await api.erase(employee.id, reference));
    } catch (failure) {
      setProblem(sentence(failure));
      setSure(false);
    } finally {
      setBusy(false);
    }
  }

  return (
    <form className="panel" onSubmit={erase}>
      <h2 className="section-title">Erase private values</h2>
      <p className="muted">
        {plural(count, "private value")} {count === 1 ? "is" : "are"} recorded about {employee.name}. Erasing
        replaces each with "[REDACTED — erasure request #…]". Their cards and the audit log stay as they are;
        the log never held the values.
      </p>
      <div className="field-row">
        <label className="field">
          <span>Erasure request number</span>
          <input
            value={reference}
            onChange={(event) => {
              setReference(event.target.value);
              setSure(false);
            }}
            placeholder="e.g. 2026-014"
            autoComplete="off"
            maxLength={40}
          />
        </label>
        <div className="action-row">
          <button
            className={sure ? "button button-danger" : "button button-quiet"}
            type="submit"
            disabled={busy || !reference.trim()}
          >
            {sure ? `Erase ${plural(count, "value")} for good` : "Erase"}
          </button>
          {sure ? (
            <button className="button button-quiet" type="button" onClick={() => setSure(false)}>
              Keep them
            </button>
          ) : null}
        </div>
      </div>
      {sure ? (
        <p className="notice-line warn" role="alert">
          This cannot be undone. Press again to erase.
        </p>
      ) : null}
      <Problem>{problem}</Problem>
    </form>
  );
}

/**
 * The temp-to-perm checkpoint (PRD 9.1): convert, extend, or begin an
 * offboarding. The 30-day check-in card is the prompt; this is where its
 * answer is acted on, because a conversion changes the person and a card
 * only changes itself.
 */
function Checkpoint({
  employee,
  onChange,
}: {
  employee: EmployeeSummary;
  onChange: (response: EmployeeResponse) => void;
}) {
  const { api } = useOrg();
  const [endDate, setEndDate] = useState("");
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);

  async function decide(request: Parameters<typeof api.checkpoint>[1]) {
    setBusy(true);
    setProblem(null);
    try {
      onChange(await api.checkpoint(employee.id, request));
      setEndDate("");
    } catch (failure) {
      setProblem(sentence(failure));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="panel">
      <h2 className="section-title">Temp-to-perm checkpoint</h2>
      <p className="muted">
        {employee.endDate
          ? `Their contract ends ${dayText(employee.endDate, true)}.`
          : "No end date is recorded for their contract."}{" "}
        Convert them, extend the contract, or begin their offboarding.
      </p>
      <div className="action-row">
        <button
          className="button"
          type="button"
          disabled={busy}
          onClick={() => void decide({ decision: "convert" })}
        >
          Convert to full-time
        </button>
        <Link
          to={{ name: "leave", employeeId: employee.id, trigger: "temp-not-converted" }}
          className="button button-quiet"
        >
          Begin offboarding
        </Link>
      </div>
      <form
        className="controls"
        onSubmit={(event: FormEvent) => {
          event.preventDefault();
          void decide({ decision: "extend", endDate });
        }}
      >
        <label className="field">
          <span>Extend until</span>
          <input
            type="date"
            value={endDate}
            min={employee.endDate ?? employee.startDate ?? undefined}
            onChange={(event) => setEndDate(event.target.value)}
          />
        </label>
        <div className="action-row">
          <button className="button button-quiet" type="submit" disabled={busy || !endDate}>
            Extend the contract
          </button>
        </div>
      </form>
      <Problem>{problem}</Problem>
    </div>
  );
}
